import React from 'react';
import { Link } from 'react-router-dom';
import {
  Facebook,
  Trophy,
  Award,
  MapPin,
  Phone,
  Mail,
  ChevronRight,
} from 'lucide-react';
import {
  TRIPADVISOR_LINKS,
  FRANKIES_FACEBOOK_URL,
  RESTAURANT_INFO,
} from '../data/restaurantData';
import { useImages } from '../context/ImageContext';
import { ClientImage } from './ClientImage';
import { TripAdvisorOwlIcon } from './TripAdvisorBanner';

export const Footer: React.FC = () => {
  const { clientImages } = useImages();

  return (
    <footer className="w-full bg-[#121316] text-white border-t border-white/10" id="contact">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-12 space-y-7">
        
        {/* Description */}
        <p className="text-sm sm:text-[15px] text-zinc-300 leading-relaxed font-normal">
          Seafront food kiosk and bar on Ramsgate Main Sands. 6oz steak burgers, proper chips, jumbo dogs and cold drinks.
        </p>

        {/* Awards Dark Rounded Box */}
        <div className="bg-[#1A1C20] border border-white/10 rounded-2xl p-4 sm:p-5 divide-y divide-white/10 shadow-xs">
          {/* Award 1 */}
          <Link
            to="/tripadvisor"
            className="flex items-start gap-3.5 pb-3.5 group cursor-pointer"
            title="TripAdvisor Best of the Best"
          >
            <Trophy className="w-4 h-4 text-[#FFD000] shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
            <span className="text-xs sm:text-[13px] text-zinc-200 group-hover:text-white transition-colors leading-snug font-medium">
              TripAdvisor Best of the Best, as featured in The Sun
            </span>
          </Link>

          {/* Award 2 */}
          <Link
            to="/about"
            className="flex items-start gap-3.5 pt-3.5 group cursor-pointer"
            title="1066 Business Awards"
          >
            <Award className="w-4 h-4 text-[#FFD000] shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
            <span className="text-xs sm:text-[13px] text-zinc-200 group-hover:text-white transition-colors leading-snug font-medium">
              1066 Business Awards, Extra Mile winner 2024
            </span>
          </Link>
        </div>

        {/* EXPLORE Section: 2 Columns */}
        <div>
          <h4 className="text-xs font-black tracking-[0.2em] uppercase text-white font-heading mb-4">
            EXPLORE
          </h4>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3.5 text-sm sm:text-[15px] text-zinc-300 font-medium">
            <Link to="/menu" className="hover:text-[#FFD000] transition-colors">
              Menu
            </Link>
            <Link to="/reviews" className="hover:text-[#FFD000] transition-colors">
              Reviews
            </Link>
            <Link to="/beach-hire" className="hover:text-[#FFD000] transition-colors">
              Beach hire
            </Link>
            <Link to="/about" className="hover:text-[#FFD000] transition-colors">
              About us
            </Link>
            <Link to="/rides" className="hover:text-[#FFD000] transition-colors">
              Rides
            </Link>
            <Link to="/charity" className="hover:text-[#FFD000] transition-colors">
              Charity
            </Link>
            <Link to="/gallery" className="hover:text-[#FFD000] transition-colors">
              Gallery
            </Link>
            <Link to="/contact" className="hover:text-[#FFD000] transition-colors">
              Contact
            </Link>
          </div>
        </div>

        {/* VISIT AND CONTACT Section */}
        <div>
          <h4 className="text-xs font-black tracking-[0.2em] uppercase text-white font-heading mb-4">
            VISIT AND CONTACT
          </h4>

          <div className="bg-[#1A1C20] border border-white/10 rounded-2xl divide-y divide-white/10 overflow-hidden shadow-xs">
            {/* 1. Address + Get directions */}
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Kiosk+1+Marina+Esplanade+Ramsgate+CT11+8LS"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 sm:p-4.5 hover:bg-white/5 transition-colors group cursor-pointer"
            >
              <div className="flex items-start gap-3.5">
                <MapPin className="w-4 h-4 text-[#FFD000] shrink-0 mt-0.5" />
                <div className="text-xs sm:text-[13px] leading-snug">
                  <p className="text-zinc-200 font-medium">Kiosk 1, Marina Esplanade</p>
                  <p className="text-zinc-400">
                    Ramsgate CT11 8LS • <span className="text-[#FFD000] font-bold group-hover:underline">Get directions</span>
                  </p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 group-hover:translate-x-0.5 transition-all shrink-0 ml-2" />
            </a>

            {/* 2. Phone */}
            <a
              href={`tel:${RESTAURANT_INFO.contact.phone.replace(/\s+/g, '')}`}
              className="flex items-center justify-between p-4 sm:p-4.5 hover:bg-white/5 transition-colors group cursor-pointer"
            >
              <div className="flex items-center gap-3.5">
                <Phone className="w-4 h-4 text-[#FFD000] shrink-0" />
                <span className="text-xs sm:text-[13px] font-bold text-zinc-200 group-hover:text-white transition-colors">
                  {RESTAURANT_INFO.contact.phone}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 group-hover:translate-x-0.5 transition-all shrink-0 ml-2" />
            </a>

            {/* 3. Email */}
            <a
              href={`mailto:${RESTAURANT_INFO.contact.email}`}
              className="flex items-center justify-between p-4 sm:p-4.5 hover:bg-white/5 transition-colors group cursor-pointer"
            >
              <div className="flex items-center gap-3.5">
                <Mail className="w-4 h-4 text-[#FFD000] shrink-0" />
                <span className="text-xs sm:text-[13px] font-semibold text-zinc-200 group-hover:text-white transition-colors break-all">
                  {RESTAURANT_INFO.contact.email}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 group-hover:translate-x-0.5 transition-all shrink-0 ml-2" />
            </a>
          </div>
        </div>

        {/* "See you at the beach!" & Social Media Icons */}
        <div className="pt-2 flex items-center justify-between gap-4">
          <p
            className="text-2xl sm:text-3xl font-extrabold italic text-[#FFD000] tracking-tight leading-tight select-none"
            style={{ fontFamily: "'Playfair Display', Georgia, cursive, serif" }}
          >
            See you at the beach!
          </p>

          <div className="flex items-center gap-2.5 shrink-0">
            {/* Facebook */}
            <a
              href={FRANKIES_FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border border-white/20 bg-white/5 flex items-center justify-center text-white/90 hover:text-white hover:bg-white/15 active:scale-95 transition-all"
              title="Follow Frankie's on Facebook"
              aria-label="Facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>

            {/* TripAdvisor Solid Green Round Badge */}
            <a
              href={TRIPADVISOR_LINKS.restaurant.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-[#34E0A1] flex items-center justify-center text-black hover:bg-[#2dc98f] active:scale-95 transition-all shadow-xs"
              title="Review Frankie's on TripAdvisor"
              aria-label="TripAdvisor"
            >
              <TripAdvisorOwlIcon className="w-6 h-6" circleBg={false} />
            </a>
          </div>
        </div>

        {/* Bottom Copyright */}
        <div className="pt-4 border-t border-white/10 text-center text-xs text-zinc-400">
          <p>© 2026 Frankie's @ the beach. All rights reserved.</p>
        </div>

      </div>
    </footer>
  );
};
